import {
  Callout,
  Card,
  Field,
  FieldError,
  RadioGroup,
  Skeleton,
} from "@/components/ui";
import { getCodeValue, getDisplay } from "@curai/js-fhir-tools";
import { useEffect } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { useCptSearch } from "../network";
import type { VisitClosureFormData, VisitClosureFormInput } from "../schemas";

const CPT_SYSTEM_URL = "http://www.ama-assn.org/go/cpt";
const CPT_FALLBACK_SYSTEM_URL =
  "https://fhir-ig.curaihealth.com/CodeSystem/claim-bill";

interface CPTCodesStepProps {
  patientId: string;
  form: UseFormReturn<VisitClosureFormInput, unknown, VisitClosureFormData>;
}

export const CPTCodesStep = ({ patientId, form }: CPTCodesStepProps) => {
  let totalEncounterTime =
    form.watch("encounterTimeData.chartingTimeSpent") +
    form.watch("encounterTimeData.othersChartingTimeSpent");

  const patientType = form.watch("encounterTimeData.patientType");
  const billType = form.watch("encounterTimeData.billType");
  const visitType = form.watch("encounterTimeData.visitType");

  const postEncounterActivities = form.watch(
    "encounterTimeData.postEncounterActivities",
  );
  for (const activity of postEncounterActivities) {
    totalEncounterTime += activity.time;
  }

  const {
    data: cptData,
    isLoading: isCptLoading,
    isError: isCptError,
    refetch: refetchCpt,
  } = useCptSearch(
    patientId,
    patientType,
    billType,
    totalEncounterTime,
    visitType,
  );

  useEffect(() => {
    if (cptData?.suggestedCode) {
      form.setValue("cptCode", cptData.suggestedCode);
    }
  }, [cptData?.suggestedCode, form.setValue]);

  const options =
    cptData?.cptCodes?.map((cpt) => {
      const codeValue =
        getCodeValue(cpt, CPT_SYSTEM_URL) ||
        getCodeValue(cpt, CPT_FALLBACK_SYSTEM_URL);
      const display = getDisplay(cpt);
      return {
        label: `${codeValue} - ${display || "Unknown CPT"}`,
        value: codeValue || "",
      };
    }) || [];

  return (
    <Card className="p-4 flex w-full flex-col items-start">
      {isCptLoading && (
        <>
          <Skeleton className="w-full h-4 mb-2" />
          <Skeleton className="w-full h-4 mb-2" />
          <Skeleton className="w-full h-4 mb-2" />
          <Skeleton className="w-full h-4" />
        </>
      )}
      {isCptError && (
        <Callout
          variant="danger"
          title="Error loading CPT codes"
          description="Please click here to try again."
          onClick={() => refetchCpt()}
        />
      )}
      {!isCptLoading && !isCptError && cptData && (
        <Controller
          control={form.control}
          name="cptCode"
          render={({ field, fieldState }) => {
            return (
              <Field data-invalid={fieldState.invalid}>
                <RadioGroup
                  orientation="vertical"
                  options={options}
                  value={field?.value?.coding?.[0]?.code}
                  onValueChange={(value) => {
                    const cpt = cptData?.cptCodes?.find(
                      (cpt) =>
                        getCodeValue(cpt, CPT_SYSTEM_URL) === value ||
                        getCodeValue(cpt, CPT_FALLBACK_SYSTEM_URL) === value,
                    );
                    if (cpt) {
                      field.onChange(cpt);
                      form.trigger("cptCode");
                    }
                  }}
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            );
          }}
        />
      )}
    </Card>
  );
};
